/**
 * <venner-countdown>
 *
 * Cuenta regresiva configurable. Reemplaza a la app de timers.
 *
 * Modos:
 *   fixed     — termina en una fecha y hora concretas
 *   daily     — se reinicia todos los días a una hora
 *   weekly    — se reinicia cada semana en un día y hora
 *   evergreen — arranca cuando el visitante llega y se recuerda en su navegador
 *
 * Las horas se interpretan siempre en la zona horaria de la tienda, que Liquid entrega como desfase
 * numérico. Así el contador marca lo mismo para un cliente en Tijuana que para uno en Cancún.
 */
if (!window.customElements.get('venner-countdown')) {
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  class VennerCountdown extends HTMLElement {
    connectedCallback() {
      this.units = {
        days: this.querySelector('[data-unit="days"] [data-value]'),
        hours: this.querySelector('[data-unit="hours"] [data-value]'),
        minutes: this.querySelector('[data-unit="minutes"] [data-value]'),
        seconds: this.querySelector('[data-unit="seconds"] [data-value]'),
      };

      this.timerElement = this.querySelector('[data-countdown-timer]');
      this.endedElement = this.querySelector('[data-countdown-ended]');
      this.dismissButton = this.querySelector('[data-countdown-dismiss]');

      if (this.dismissButton) {
        this.dismissButton.addEventListener('click', this.dismiss.bind(this));

        if (this.storage.get(this.dismissKey)) {
          this.remove();
          return;
        }
      }

      this.target = this.resolveTarget();
      this.tick();
      this.interval = window.setInterval(this.tick.bind(this), SECOND);

      // Al volver de una pestaña en segundo plano el navegador pausa los timers: recalcular al reenfocar
      // evita que el contador se quede atrasado.
      this.onVisibility = () => {
        if (!document.hidden) {
          this.target = this.resolveTarget();
          this.tick();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get mode() {
      return this.dataset.mode || 'fixed';
    }

    get behavior() {
      return this.dataset.behavior || 'hide';
    }

    get offsetMinutes() {
      const raw = this.dataset.shopOffset || '+0000';
      const sign = raw.startsWith('-') ? -1 : 1;
      const hours = parseInt(raw.slice(1, 3), 10) || 0;
      const minutes = parseInt(raw.slice(3, 5), 10) || 0;

      return sign * (hours * 60 + minutes);
    }

    get storage() {
      // Safari en modo privado lanza al escribir en localStorage; el contador debe seguir funcionando.
      return {
        get: (key) => {
          try {
            return window.localStorage.getItem(key);
          } catch (error) {
            return null;
          }
        },
        set: (key, value) => {
          try {
            window.localStorage.setItem(key, value);
          } catch (error) {
            /* sin persistencia: el contador arranca de nuevo en cada visita */
          }
        },
      };
    }

    get dismissKey() {
      return `venner-countdown-dismissed-${this.dataset.uid}`;
    }

    get evergreenKey() {
      return `venner-countdown-start-${this.dataset.uid}`;
    }

    /** Momento actual desplazado para que los getters UTC devuelvan la hora de la tienda. */
    shopNow(nowUtc) {
      return new Date(nowUtc + this.offsetMinutes * MINUTE);
    }

    /** Convierte una hora local de la tienda a marca de tiempo real. */
    shopTimeToUtc(year, month, date, hours, minutes) {
      return Date.UTC(year, month, date, hours, minutes, 0) - this.offsetMinutes * MINUTE;
    }

    parseClock(value, fallback) {
      const [hours, minutes] = String(value || fallback).split(':');

      return [parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0];
    }

    resolveTarget() {
      const nowUtc = Date.now();

      switch (this.mode) {
        case 'daily': {
          const [hours, minutes] = this.parseClock(this.dataset.at, '23:59');
          const shop = this.shopNow(nowUtc);
          let target = this.shopTimeToUtc(shop.getUTCFullYear(), shop.getUTCMonth(), shop.getUTCDate(), hours, minutes);

          if (target <= nowUtc) {
            target += DAY;
          }

          return target;
        }

        case 'weekly': {
          const [hours, minutes] = this.parseClock(this.dataset.at, '23:59');
          const weekday = parseInt(this.dataset.weekday, 10) || 0;
          const shop = this.shopNow(nowUtc);
          let target = this.shopTimeToUtc(shop.getUTCFullYear(), shop.getUTCMonth(), shop.getUTCDate(), hours, minutes);

          let ahead = (weekday - shop.getUTCDay() + 7) % 7;

          if (ahead === 0 && target <= nowUtc) {
            ahead = 7;
          }

          return target + ahead * DAY;
        }

        case 'evergreen': {
          const window_ = (parseInt(this.dataset.minutes, 10) || 15) * MINUTE;
          let start = parseInt(this.storage.get(this.evergreenKey), 10);

          if (!start || Number.isNaN(start) || (this.behavior === 'restart' && start + window_ <= nowUtc)) {
            start = nowUtc;
            this.storage.set(this.evergreenKey, String(start));
          }

          return start + window_;
        }

        default: {
          const parsed = Date.parse(this.dataset.target);

          return Number.isNaN(parsed) ? nowUtc : parsed;
        }
      }
    }

    tick() {
      const remaining = this.target - Date.now();

      if (remaining <= 0) {
        // En los modos cíclicos el siguiente objetivo se calcula solo, sin recargar la página.
        if (this.mode === 'daily' || this.mode === 'weekly') {
          this.target = this.resolveTarget();
          return this.tick();
        }

        this.render(0);
        return this.onExpired();
      }

      this.render(remaining);
    }

    render(remaining) {
      const showDays = Boolean(this.units.days);
      const days = showDays ? Math.floor(remaining / DAY) : 0;
      const hours = Math.floor((remaining - days * DAY) / HOUR);
      const minutes = Math.floor((remaining % HOUR) / MINUTE);
      const seconds = Math.floor((remaining % MINUTE) / SECOND);

      this.write(this.units.days, days);
      this.write(this.units.hours, hours);
      this.write(this.units.minutes, minutes);
      this.write(this.units.seconds, seconds);
    }

    write(element, value) {
      if (!element) {
        return;
      }

      const text = String(value).padStart(2, '0');

      if (element.textContent !== text) {
        element.textContent = text;
      }
    }

    onExpired() {
      window.clearInterval(this.interval);

      if (this.behavior === 'hide') {
        this.remove();
        return;
      }

      if (this.behavior === 'message' && this.endedElement) {
        this.timerElement?.setAttribute('hidden', '');
        this.endedElement.removeAttribute('hidden');
      }

      if (this.behavior === 'restart' && this.mode === 'evergreen') {
        this.target = this.resolveTarget();
        this.interval = window.setInterval(this.tick.bind(this), SECOND);
      }
    }

    dismiss() {
      this.storage.set(this.dismissKey, '1');
      this.remove();
    }
  }

  window.customElements.define('venner-countdown', VennerCountdown);
}
